import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/common/ProtectedRoute";

// Pages
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { DashboardPage } from "./pages/DashboardPage";
import { DossiersPage } from "./pages/DossiersPage";
import { DossierFormPage } from "./pages/DossierFormPage";
import { FormationsPage } from "./pages/FormationsPage";
import { FormateursPage } from "./pages/FormateursPage";
import { CandidatsPage } from "./pages/CandidatsPage";
import SocietesPage from "./pages/SocietesPage";
import { PlanningPage } from "./pages/PlanningPage";
import { FacturationPage } from "./pages/FacturationPage";
import { ModulesPage } from "./pages/ModulesPage";
import { JourneeDetailPage } from "./pages/JourneeDetailPage";
import CandidatActionPage from "./pages/CandidatActionPage";
import { RequetesPage } from "./pages/RequetesPage";
import { PortailFormateurPage } from "./pages/PortailFormateurPage";
import { QCMPage } from "./pages/QCMPage";
import { QCMPassagePage } from "./pages/QCMPassagePage";
import QCMPublicPage from "./pages/QCMPublicPage";
import { PortailCandidatPage } from "./pages/PortailCandidatPage";
import PortailClientPage from "./pages/PortailClientPage";
import PortailFinanceurPage from "./pages/PortailFinanceurPage";
import UsersPage from "./pages/UsersPage";
import { PortailResponsablePage } from "./pages/PortailResponsablePage";
import EspaceCommercialPage from "./pages/EspaceCommercialPage";
import PortailCommercialPage from "./pages/PortailCommercialPage";

// /commercial : portail du commercial ou espace commercial admin selon le rôle
const CommercialSwitch = () => {
  const { user } = useAuth();
  return user?.role === 'commercial' ? <PortailCommercialPage /> : <EspaceCommercialPage />;
};
import { MonPlanningPage } from "./pages/MonPlanningPage";
import { MonActivitePage } from "./pages/MonActivitePage";
import { AdminSettingsPage } from "./pages/AdminSettingsPage";
import PrestationsPage from "./pages/PrestationsPage";
import FacturationChainePage from "./pages/FacturationChainePage";
import PrestationDetailPage from "./pages/PrestationDetailPage";
import DossierPlanningPage from "./pages/DossierPlanningPage";
import DossierApercuPage from "./pages/DossierApercuPage";
import SupportPedaPage from "./pages/SupportPedaPage";
import MonProfilPage from "./pages/MonProfilPage";
import ListesPage from "./pages/ListesPage";
import { usePwaManifest } from "./hooks/usePwaManifest";

import "./App.css";

const PwaManifestSwitcher = () => { usePwaManifest(); return null; };

const DefaultRedirect = () => {
  const role = localStorage.getItem('userRole');
  if (role === 'formateur') return <Navigate to="/portail-formateur" replace />;
  if (role === 'candidat') return <Navigate to="/portail-candidat" replace />;
  if (role === 'client') return <Navigate to="/portail-client" replace />;
  if (role === 'financeur') return <Navigate to="/portail-financeur" replace />;
  if (role === 'commercial') return <Navigate to="/commercial" replace />;
  if (role === 'responsable_pedagogique') return <Navigate to="/portail-responsable" replace />;
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PwaManifestSwitcher />
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login-financeur" element={<LoginPage espaceForce="financeur" />} />
          <Route path="/login-entreprise" element={<LoginPage espaceForce="client" />} />
          <Route path="/login-candidat" element={<LoginPage espaceForce="candidat" />} />
          <Route path="/login-formateur" element={<LoginPage espaceForce="formateur" />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dossiers"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <DossiersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dossiers/new"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <DossierFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dossiers/:id/planning"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <DossierPlanningPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/facturation/chaine"
            element={
              <ProtectedRoute allowedRoles={["admin", "administratif"]}>
                <FacturationChainePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prestations"
            element={
              <ProtectedRoute allowedRoles={["admin", "administratif"]}>
                <PrestationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/prestations/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "administratif"]}>
                <PrestationDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dossiers/:id"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <DossierApercuPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dossiers/:id/edit"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <DossierFormPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dossiers/:dossierId/candidat/:candidatId"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif", "formateur"]}>
                <CandidatActionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dossiers/:dossierId/journee/:date"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <JourneeDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/formations"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <FormationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/modules"
            element={<Navigate to="/support-peda" replace />}
          />
          <Route
            path="/support-peda"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif", "formateur"]}>
                <SupportPedaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/listes"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <ListesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mon-profil"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif", "formateur", "candidat", "commercial"]}>
                <MonProfilPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/formateurs"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <FormateursPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/candidats"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <CandidatsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/societes"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <SocietesPage />
              </ProtectedRoute>
            }
          />
          <Route path="/clients" element={<Navigate to="/societes" replace />} />
          <Route path="/financeurs" element={<Navigate to="/societes" replace />} />
          <Route
            path="/planning"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <PlanningPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/facturation"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <FacturationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/requetes"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <RequetesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portail-formateur"
            element={
              <ProtectedRoute allowedRoles={["admin", "formateur", "responsable_pedagogique", "administratif"]}>
                <PortailFormateurPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portail-candidat"
            element={
              <ProtectedRoute allowedRoles={["admin", "candidat"]}>
                <PortailCandidatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portail-client"
            element={
              <ProtectedRoute allowedRoles={["admin", "client", "financeur"]}>
                <PortailClientPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portail-financeur"
            element={
              <ProtectedRoute allowedRoles={["admin", "client", "financeur"]}>
                <PortailFinanceurPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/utilisateurs"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portail-responsable"
            element={
              <ProtectedRoute allowedRoles={["admin", "responsable_pedagogique", "administratif"]}>
                <PortailResponsablePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/qcm"
            element={<Navigate to="/support-peda" replace />}
          />
          <Route
            path="/qcm/passage/:qcmId"
            element={
              <ProtectedRoute allowedRoles={["admin", "formateur", "candidat"]}>
                <QCMPassagePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/commercial"
            element={
              <ProtectedRoute allowedRoles={["admin", "commercial", "responsable_pedagogique"]}>
                <CommercialSwitch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/portail-commercial"
            element={
              <ProtectedRoute allowedRoles={["admin", "commercial", "responsable_pedagogique"]}>
                <PortailCommercialPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/mon-planning"
            element={
              <ProtectedRoute allowedRoles={["admin", "formateur"]}>
                <MonPlanningPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/mon-activite"
            element={
              <ProtectedRoute allowedRoles={["admin", "formateur", "responsable_pedagogique", "administratif", "commercial"]}>
                <MonActivitePage />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={["admin"]}>
                <AdminSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/qcm/public" element={<QCMPublicPage />} />
          <Route path="/" element={<DefaultRedirect />} />
          <Route path="*" element={<DefaultRedirect />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
